import { supabase } from '../../shared/supabase/supabaseClient';
import type { DeliveryZoneRow } from '@shared/deliveryTypes';
import {
  invalidateStorefrontStatus,
  invalidateStorefrontZones,
} from './storefrontStatusCache';

type ZoneInsert = {
  branch_id: string;
  organization_id: string;
  name: string;
  zone_type: 'radius' | 'polygon';
  radius_meters: number;
  area: string | null;
  delivery_fee: number;
  min_order_amount: number;
  estimated_time_minutes: number;
  is_active: boolean;
  sort_order: number;
};

type ZoneUpdate = Partial<Omit<ZoneInsert, 'branch_id' | 'organization_id'>> & {
  updated_at: string;
};

export async function listDeliveryZones(branchId: string): Promise<DeliveryZoneRow[]> {
  const { data, error } = await supabase
    .from('delivery_zones')
    .select('*')
    .eq('branch_id', branchId)
    .order('sort_order', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as DeliveryZoneRow[];
}

export async function createDeliveryZone(
  branchId: string,
  orgId: string,
  params: {
    name: string;
    zoneType: 'radius' | 'polygon';
    radiusMeters: number;
    area: string | null;
    deliveryFee: number;
    minOrderAmount: number;
    estimatedTimeMinutes: number;
    sortOrder: number;
  },
): Promise<DeliveryZoneRow> {
  const insert: ZoneInsert = {
    branch_id: branchId,
    organization_id: orgId,
    name: params.name,
    zone_type: params.zoneType,
    radius_meters: params.radiusMeters,
    area: params.area,
    delivery_fee: params.deliveryFee,
    min_order_amount: params.minOrderAmount,
    estimated_time_minutes: params.estimatedTimeMinutes,
    is_active: true,
    sort_order: params.sortOrder,
  };
  const { data, error } = await supabase
    .from('delivery_zones')
    .insert(insert)
    .select()
    .single();
  if (error) throw new Error(error.message);
  invalidateStorefrontZones(branchId);
  return data as DeliveryZoneRow;
}

export async function updateDeliveryZone(
  zoneId: string,
  patch: Partial<{
    name: string;
    zoneType: 'radius' | 'polygon';
    radiusMeters: number;
    area: string | null;
    deliveryFee: number;
    minOrderAmount: number;
    estimatedTimeMinutes: number;
    isActive: boolean;
    sortOrder: number;
  }>,
): Promise<void> {
  const update: ZoneUpdate = {
    updated_at: new Date().toISOString(),
  };
  if (patch.name !== undefined) update.name = patch.name;
  if (patch.zoneType !== undefined) update.zone_type = patch.zoneType;
  if (patch.radiusMeters !== undefined) update.radius_meters = patch.radiusMeters;
  if (patch.area !== undefined) update.area = patch.area;
  if (patch.deliveryFee !== undefined) update.delivery_fee = patch.deliveryFee;
  if (patch.minOrderAmount !== undefined) update.min_order_amount = patch.minOrderAmount;
  if (patch.estimatedTimeMinutes !== undefined) update.estimated_time_minutes = patch.estimatedTimeMinutes;
  if (patch.isActive !== undefined) update.is_active = patch.isActive;
  if (patch.sortOrder !== undefined) update.sort_order = patch.sortOrder;

  const { error } = await supabase
    .from('delivery_zones')
    .update(update)
    .eq('id', zoneId);
  if (error) throw new Error(error.message);
  invalidateStorefrontZones();
}

export async function deleteDeliveryZone(zoneId: string): Promise<void> {
  const { error } = await supabase
    .from('delivery_zones')
    .delete()
    .eq('id', zoneId);
  if (error) throw new Error(error.message);
  invalidateStorefrontZones();
}

export async function setDeliveryModes(
  branchId: string,
  modes: { ownDeliveryEnabled: boolean; vueltoRidersEnabled: boolean },
): Promise<void> {
  const { error } = await supabase
    .from('public_store_profiles')
    .update({
      own_delivery_enabled: modes.ownDeliveryEnabled,
      vuelto_riders_enabled: modes.vueltoRidersEnabled,
      updated_at: new Date().toISOString(),
    })
    .eq('branch_id', branchId);
  if (error) throw new Error(error.message);
  invalidateStorefrontStatus(branchId);
}
